import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Star,
  Crown,
  MoreVertical,
  Trash2,
  Loader2,
  BarChart3,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDeleteDialog } from "@/components/shared/ConfirmDeleteDialog";
import { toast } from "@/hooks/use-toast";
import { KeywordMasterListAction } from "@/constants";
import { useRemoveKeywordFromTheme, useUpdateKeywordInTheme, useAddKeywordToTheme, keywordThemesQueryKey } from "@/resources/keyword-theme";
import queryClient from "@/query-client";
import { HeadTermBadge, ThemePickerSubmenu, ThemeColumnCell } from "./KeywordThemeShared";
import { TruncatedText } from "@/components/ui/truncated-text";
import { AmazonSearchLink } from "@/components/shared/AmazonSearchLink";
import { KeywordMetricCells, KeywordTagCells } from "./KeywordMetricCells";
import { UtilizationTag } from "@/types/keyword";
import type { KeywordTheme, KeywordThemeKeyword } from "@/types/keyword-theme";
import type { Keyword } from "@/types/keyword";

export function KeywordRow({
  keyword,
  themeKeyword,
  isInTheme,
  themeId,
  productId,
  themes,
  selected,
  onSelectKeyword,
  onUpdateMasterList,
  onDeleteKeyword,
  onOpenLandscape,
  onOpenAddTheme,
  onAddToRankTracker,
  onRemoveFromRankTracker,
  readOnly,
  indent,
  stickyStyle,
  stickyGrid,
  keywordTextWidth,
}: {
  keyword: Keyword;
  themeKeyword?: KeywordThemeKeyword;
  isInTheme: boolean;
  themeId?: number;
  productId: number;
  themes: KeywordTheme[];
  selected?: boolean;
  onSelectKeyword?: (keywordId: number, selected: boolean) => void;
  onUpdateMasterList: (keywordIds: number[], action: KeywordMasterListAction) => void;
  onDeleteKeyword: (keywordId: number) => void;
  onOpenLandscape?: (landscapeId: number) => void;
  onOpenAddTheme?: (keywordId?: number) => void;
  onAddToRankTracker: (keyword: Keyword) => void;
  onRemoveFromRankTracker: (keyword: Keyword) => void;
  readOnly?: boolean;
  indent?: boolean;
  stickyStyle: React.CSSProperties;
  stickyGrid: string;
  keywordTextWidth: number;
}) {
  const [isPending, setIsPending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmUnassign, setConfirmUnassign] = useState(false);
  const { mutateAsync: removeFromTheme } = useRemoveKeywordFromTheme();
  const { mutateAsync: addToTheme } = useAddKeywordToTheme();
  const { mutateAsync: updateKeywordInTheme } = useUpdateKeywordInTheme();

  const currentThemeName = themes.find((t) => t.id === themeId)?.name;

  const toggleMasterList = () => {
    onUpdateMasterList(
      [keyword.id],
      keyword.is_master_list ? KeywordMasterListAction.REMOVE : KeywordMasterListAction.ADD,
    );
  };

  const handleRemoveFromTheme = async () => {
    if (!themeId || isPending) return;
    setIsPending(true);
    try { await removeFromTheme({ productId, themeId, keywordId: keyword.id }); }
    finally {
      setIsPending(false);
      setConfirmUnassign(false);
    }
  };

  const handleChangeTheme = async (toThemeId: number) => {
    if (isPending) return;
    setIsPending(true);

    const queryKey = keywordThemesQueryKey(productId);
    const previous = queryClient.getQueryData<KeywordTheme[]>(queryKey);
    queryClient.setQueryData<KeywordTheme[]>(queryKey, (old) => {
      if (!old) return old;
      return old.map((t) => {
        if (t.id === themeId) {
          return { ...t, keywords: t.keywords.filter((k) => k.keyword_id !== keyword.id) };
        }
        if (t.id === toThemeId) {
          return { ...t, keywords: [...t.keywords, { id: 0, keyword_id: keyword.id, is_head_term: false, search: keyword.search, rank: null, kps: null, kps_raw: keyword.kps_raw ?? null, search_volume: keyword.search_volume, hero_relevance: null, search_volume_trend: keyword.search_volume_trend ?? null }] };
        }
        return t;
      });
    });

    try {
      await addToTheme({ productId, themeId: toThemeId, keywordId: keyword.id });
      const targetThemeName = themes.find((t) => t.id === toThemeId)?.name ?? "theme";
      toast({ title: "Keyword moved", description: `Moved to "${targetThemeName}".` });
    } catch {
      queryClient.setQueryData(queryKey, previous);
    } finally {
      setIsPending(false);
    }
  };

  const handleSetHeadTerm = async (value: boolean) => {
    if (!themeId || isPending) return;
    setIsPending(true);
    try { await updateKeywordInTheme({ productId, themeId, keywordId: keyword.id, is_head_term: value }); }
    finally { setIsPending(false); }
  };

  return (
    <>
      <TableRow
        className={cn("transition-opacity", isPending && "opacity-50 pointer-events-none")}
      >
        <TableCell sticky style={stickyStyle} className="py-0 px-0">
          <div className={stickyGrid}>
            {!readOnly && (
              <span className="py-2 px-2 flex items-center justify-center">
                <Checkbox
                  checked={!!selected}
                  onCheckedChange={(checked) => onSelectKeyword?.(keyword.id, !!checked)}
                  aria-label={`Select ${keyword.search}`}
                />
              </span>
            )}
            <span className="py-2 flex items-center justify-center">
              {!readOnly ? (
                <button
                  type="button"
                  onClick={toggleMasterList}
                  className={cn(
                    "transition-colors",
                    keyword.is_master_list
                      ? "text-amber-400 hover:text-amber-500"
                      : "text-muted-foreground/40 hover:text-amber-400",
                  )}
                >
                  <Star className={cn("h-4 w-4", keyword.is_master_list && "fill-current")} />
                </button>
              ) : (
                <Star
                  className={cn("h-4 w-4", keyword.is_master_list ? "text-amber-400 fill-current" : "text-muted-foreground/40")}
                />
              )}
            </span>
            {/* AUT-143/144: full keyword on hover once it's actually truncated. */}
            <span className={cn("py-2 px-3 text-sm font-medium flex items-center gap-1.5 min-w-0", indent && "pl-7")}>
              <TruncatedText width={keywordTextWidth} className="font-medium flex-1 min-w-0">{keyword.search}</TruncatedText>
              {themeKeyword?.is_head_term && <HeadTermBadge />}
              {/* AUT-496: `order-last` keeps it right of the Head Term badge, `ml-auto` lines the
                  icons up across rows whether or not a row carries one. */}
              <AmazonSearchLink keyword={keyword.search} className="order-last ml-auto" />
            </span>
          </div>
        </TableCell>
        <KeywordMetricCells keyword={keyword} />
        <TableCell className="w-[115px] py-2 px-2">
          <div className="flex items-center justify-center">
            <ThemeColumnCell
              themeName={isInTheme && themeId ? (themes.find((t) => t.id === themeId)?.name) : undefined}
              themes={themes}
              keywordId={keyword.id}
              onSelect={handleChangeTheme}
              onOpenAddTheme={onOpenAddTheme}
              readOnly={readOnly}
            />
          </div>
        </TableCell>
        <KeywordTagCells keyword={keyword} onOpenLandscape={onOpenLandscape} />
        {!readOnly && (
          <TableCell className="w-[70px] py-1.5 px-2">
            <div className="flex items-center justify-center">
              {isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6" disabled={isPending}>
                      <MoreVertical className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover z-50 rounded-[8px]">
                    <DropdownMenuItem
                      onClick={() =>
                        keyword.utilizationTags?.includes(UtilizationTag.TRACKED)
                          ? onRemoveFromRankTracker(keyword)
                          : onAddToRankTracker(keyword)
                      }
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      {keyword.utilizationTags?.includes(UtilizationTag.TRACKED)
                        ? "Remove from Rank Tracker"
                        : "Add to Rank Tracker"}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {isInTheme && themeId && (
                      <>
                        {themeKeyword?.is_head_term ? (
                          <DropdownMenuItem onClick={() => handleSetHeadTerm(false)}>
                            <Crown className="h-4 w-4 mr-2" />
                            Remove from head term
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => handleSetHeadTerm(true)}>
                            <Crown className="h-4 w-4 mr-2" />
                            Make head term
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                      </>
                    )}
                    {themes.length > 0 && (
                      <>
                        <ThemePickerSubmenu
                          themes={themes}
                          currentThemeId={themeId}
                          onSelect={handleChangeTheme}
                          onRemove={isInTheme ? () => setConfirmUnassign(true) : undefined}
                        />
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={toggleMasterList}>
                      <Star className="h-4 w-4 mr-2" />
                      {keyword.is_master_list ? "Remove from Master List" : "Add to Master List"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setConfirmDelete(true)} className="text-destructive focus:text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete keyword
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </TableCell>
        )}
      </TableRow>

      <ConfirmDeleteDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={`Delete "${keyword.search}"?`}
        description="This will permanently remove this keyword from the keyword bank. This action cannot be undone."
        onConfirm={() => {
          onDeleteKeyword(keyword.id);
          setConfirmDelete(false);
        }}
      />

      <ConfirmDeleteDialog
        open={confirmUnassign}
        onOpenChange={setConfirmUnassign}
        title={`Remove "${keyword.search}" from "${currentThemeName}"?`}
        description="This keyword will become unthemed. It will stay in the keyword bank."
        confirmLabel="Remove"
        isPending={isPending}
        onConfirm={handleRemoveFromTheme}
      />
    </>
  );
}
